<template>
  <div class="home-page">
    <div class="intro-section">
      <h2>{{ t({
        fr: 'Soumission de données sur les ouvrages de protection contre les inondations',
        en: 'Submission of flood protection system data'
      }) }}</h2>

      <div class="intro-content">
        <h3>{{ t({ fr: 'Mise en contexte', en: 'Context' }) }}</h3>
        <p>{{ t({
          fr: "Ressources naturelles Canada (RNCan) a mandaté AECOM pour développer un inventaire pancanadien consolidé des ouvrages de protection contre les inondations (digues, murs de protection, canaux de dérivation). Ce formulaire vise à recueillir et à consolider les jeux de données existants provenant des ministères, provinces, territoires, municipalités, offices de protection de la nature et autres partenaires.",
          en: 'Natural Resources Canada (NRCan) has contracted AECOM to develop a consolidated Canada-wide inventory of flood protection systems. Flood protection systems, or flood defence works, include levees, dikes, floodwalls and other engineered structures designed to reduce flood risk to communities, infrastructure and critical assets. This form is used to collect and consolidate existing datasets from departments, provinces, territories, municipalities, conservation authorities and other partners.'
        }) }}</p>
        <p>{{ t({
          fr: "Vous êtes invités à soumettre vos données de façon structurée. Le formulaire recueille des métadonnées standardisées et vous permet de téléverser vos fichiers en toute sécurité. Les soumissions incomplètes ou progressives sont acceptées.",
          en: 'You are invited to contribute to the inventory by submitting your data. This form collects standardized metadata and lets you upload your files securely. Incomplete or progressive submissions are accepted.'
        }) }}</p>

        <StartSurveyButton />

        <p>{{ t({
          fr: "Ce formulaire prend environ 10 minutes à remplir, sans compter le téléversement des fichiers.",
          en: 'This form takes about 10 minutes to complete, not counting file upload.'
        }) }}</p>

        <div class="contact-info">
          <p><strong>{{ t({
            fr: "Pour toute question ou pour obtenir de l'aide sur la soumission de vos données, écrivez à l'équipe de projet d'AECOM par courriel à :",
            en: "For any question or help submitting your data, contact AECOM's project team by email at:"
          }) }}</strong></p>
          <div class="contact-person">
            <a href="mailto:flood_defense_inondation@aecom.com">flood_defense_inondation@aecom.com</a>
          </div>
        </div>

        <div class="additional-info">
          <h3>{{ t({ fr: 'Informations supplémentaires', en: 'Additional information' }) }}</h3>
          <div class="info-links">
            <router-link to="/about-aecom" class="info-link">
              <div class="info-card">
                <h4>{{ t({ fr: "À propos d'AECOM", en: 'About AECOM' }) }}</h4>
                <p>{{ t({
                  fr: "Découvrez le rôle d'AECOM dans ce mandat confié par Ressources naturelles Canada.",
                  en: "Learn about AECOM's role in this mandate from Natural Resources Canada."
                }) }}</p>
              </div>
            </router-link>

            <router-link to="/privacy" class="info-link">
              <div class="info-card">
                <h4>{{ t({ fr: 'Protection des données', en: 'Data protection' }) }}</h4>
                <p>{{ t({
                  fr: 'Consultez nos engagements en matière de protection des données et de confidentialité.',
                  en: 'Read our data-protection and confidentiality commitments.'
                }) }}</p>
              </div>
            </router-link>
          </div>
        </div>

        <StartSurveyButton />
      </div>
    </div>
  </div>
</template>

<script>
import StartSurveyButton from '../components/StartSurveyButton.vue'
import { t } from '../i18n/index.js'

export default {
  name: 'HomePage',
  components: { StartSurveyButton },
  methods: { t }
}
</script>

<style scoped>
.home-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 30px 20px;
}

.intro-section {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 53, 62, 0.1);
  padding: 40px;
  overflow: hidden;
}

.intro-section h2 {
  color: var(--aecom-midnight-teal);
  font-size: 1.7em;
  text-align: center;
  border-bottom: 3px solid var(--aecom-lime-green);
  padding-bottom: 15px;
  margin: 0 0 20px 0;
}

.intro-content {
  line-height: 1.6;
}

.intro-content h3 {
  color: var(--aecom-midnight-teal);
  font-size: 1.3rem;
  margin-bottom: 15px;
  padding-bottom: 8px;
}

.intro-content p {
  color: var(--aecom-midnight-teal);
  margin-bottom: 20px;
  text-align: justify;
}

.contact-info {
  background-color: #f8f9fa;
  border-left: 4px solid var(--aecom-lime-green);
  padding: 20px;
  margin: 25px 0;
  border-radius: 4px;
}

.contact-person {
  margin: 15px 0;
}

.contact-person ul {
  margin: 10px 0;
  padding-left: 20px;
}

.contact-person li {
  margin: 5px 0;
}

.contact-info a {
  color: var(--aecom-earth-green);
  text-decoration: none;
  font-weight: 500;
}

.contact-info a:hover {
  color: var(--aecom-midnight-teal);
  text-decoration: underline;
}

.additional-info {
  margin: 30px 0;
  padding: 25px 0;
}

.additional-info h3 {
  color: var(--aecom-midnight-teal);
  font-size: 1.3rem;
  margin-bottom: 20px;
  border-bottom: 2px solid var(--aecom-lime-green);
  padding-bottom: 8px;
}

.info-links {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.info-link {
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease;
}

.info-link:hover {
  transform: translateY(-3px);
}

.info-card {
  background: linear-gradient(135deg, rgba(0, 135, 104, 0.05) 0%, rgba(174, 204, 83, 0.05) 100%);
  border: 2px solid rgba(0, 135, 104, 0.1);
  border-radius: 8px;
  padding: 12px 14px;
  transition: all 0.3s ease;
  height: 100%;
}

.info-card:hover {
  border-color: var(--aecom-earth-green);
  box-shadow: 0 4px 15px rgba(0, 135, 104, 0.15);
}

.info-card h4 {
  color: var(--aecom-earth-green);
  font-size: 1.1rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.info-card p {
  color: #5f909a;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
}

@media (max-width: 768px) {
  .home-page {
    padding: 20px 15px;
  }
  .intro-section {
    padding: 24px;
  }
  .intro-section h2 {
    font-size: 1.3rem;
  }
  .info-links {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}
</style>
